import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
  status: 'completed' | 'current' | 'upcoming';
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <motion.div
              className={`relative cursor-pointer ${
                step.status === 'completed' || step.status === 'current' ? 'cursor-pointer' : 'cursor-not-allowed'
              }`}
              onClick={() => onStepClick(step.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Step circle */}
              <div className={`
                relative w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300
                ${step.status === 'completed' 
                  ? 'bg-success-500 border-success-500' 
                  : step.status === 'current'
                  ? 'bg-primary-500 border-primary-500'
                  : 'bg-gray-100 border-gray-300'
                }
              `}>
                {step.status === 'completed' ? (
                  <Check className="w-6 h-6 text-white" />
                ) : (
                  <step.icon className={`w-6 h-6 ${
                    step.status === 'current' 
                      ? 'text-white' 
                      : 'text-gray-400'
                  }`} />
                )}
                
                {/* Pulse animation for current step */}
                {step.status === 'current' && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary-500 opacity-25"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
              </div>
              
              {/* Step info */}
              <div className="mt-3 text-center">
                <h3 className={`text-sm font-medium ${
                  step.status === 'completed' || step.status === 'current'
                    ? 'text-gray-900'
                    : 'text-gray-500'
                }`}>
                  {step.title}
                </h3>
                <p className={`text-xs mt-1 ${
                  step.status === 'completed' || step.status === 'current'
                    ? 'text-gray-600'
                    : 'text-gray-400'
                }`}>
                  {step.description}
                </p>
              </div>
            </motion.div>
            
            {/* Connection line */}
            {index < steps.length - 1 && (
              <div className="flex-1 h-0.5 mx-4 relative">
                <div className="h-full bg-gray-200 rounded-full" />
                <motion.div
                  className="h-full bg-primary-500 rounded-full absolute top-0 left-0"
                  initial={{ width: '0%' }}
                  animate={{ 
                    width: step.status === 'completed' ? '100%' : '0%' 
                  }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepIndicator;